import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useCommonApi } from '../../api';
import { useAuth } from '../hooks';
import { useDashboardRange } from './DashboardRangeContext';

const DATE_KEY_FORMAT = 'yyyy-MM-dd';

export interface DailyCount {
  accepted: number;
  rejected: number;
  total: number;
  spotSolved: number;
}

const EMPTY_DAY: DailyCount = { accepted: 0, rejected: 0, total: 0, spotSolved: 0 };

export const toDateKey = (date: Date) => format(date, DATE_KEY_FORMAT);

/** Dealer statistics for the range picked on the dashboard, indexed by yyyy-MM-dd so widgets can fill gaps with zeros. */
export const useRangeDealerStatistics = () => {
  const { user } = useAuth();
  const api = useCommonApi();
  const { range, days } = useDashboardRange();

  const { data, isLoading } = useQuery({
    ...api.getDealerStatistics({ dateFrom: range[0], dateTo: range[1], dealerId: user?.id as string }),
    enabled: Boolean(user?.id)
  });

  const byDay = useMemo(() => {
    const map = new Map<string, DailyCount>();
    data?.period?.forEach(({ date, values }) => {
      map.set(String(date).slice(0, 10), {
        accepted: values.countAccepted,
        rejected: values.countRejected,
        total: values.countTotal,
        spotSolved: values.countSpotSolved
      });
    });
    return map;
  }, [data]);

  const getDay = (key: string) => byDay.get(key) ?? EMPTY_DAY;
  const counts = days.map((day) => getDay(toDateKey(day)));

  return { days, counts, isLoading };
};
